import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import io from "socket.io-client";
import axios from "axios";
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from "@react-google-maps/api";
import BatteryStatus from "../../BatteryBMS/BatteryStatus";
import DataMonitoring from "../../BatteryBMS/DataMonitoring";
import GPSInfo from "../../LiveLocation/GPSInfo";
import DeviceHistory from "./DeviceHistory";
import libraries from "../../../../constant/jsAPILoaderLibrary";

const socket = io(process.env.REACT_APP_SOCKET_URL);

const defaultCoordinate = { lat: 16.544845, lng: 81.245645 };

const DeviceInfo = () => {
  const { deviceId } = useParams();

  const [activeTab, setActiveTab] = useState("live-data");

  const [lastUpdatedSocketData, setLastUpdatedSocketData] = useState({});
  const [lastUpdatedData, setLastUpdatedData] = useState(null);
  const [lastUpdatedTime, setLastUpdatedTime] = useState("");

  const [gpsData, setGpsData] = useState(null);
  const [gpsUpdatedTime, setGpsUpdatedTime] = useState("");
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY,
    libraries,
  });

  useEffect(() => {
    socket.on("mqtt-message", (data) => {
      setLastUpdatedTime(data.time);
      setLastUpdatedSocketData(data.bmsData);
    });

    const handleGpsData = (data) => {
      const deviceTopics = [`tele/${deviceId}`, `update/${deviceId}`];
      const latitude = parseFloat(data.gpsData?.gpsValues?.latitute);
      const longitude = parseFloat(data.gpsData?.gpsValues?.longitude);

      if (
        !deviceTopics.includes(data.topic) ||
        !Number.isFinite(latitude) ||
        !Number.isFinite(longitude)
      ) return;

      setGpsData(data.gpsData);
      setGpsUpdatedTime(data.time);
    };

    socket.on("mqtt-gps", handleGpsData);

    return () => socket.off("mqtt-gps", handleGpsData);
  }, [deviceId]);

  useEffect(() => {
    if (deviceId) {
      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}api/data?topic=update/${deviceId}`
        )
        .then(
          (res) => {
            // seed with last known data until the live "mqtt-message" socket event arrives
            setLastUpdatedSocketData((prev) =>
              prev && Object.keys(prev).length > 0 ? prev : res.data
            );
          },
          (error) => {
            console.log(error);
          }
        );

      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}api/v1/bms/device-data?topic=update/${deviceId}`
        )
        .then(
          (res) => {
            setLastUpdatedData(res?.data?.data);
          },
          (error) => {
            console.log(error);
          }
        );
    }
  }, [deviceId]);

  const latitude = parseFloat(gpsData?.gpsValues?.latitute);
  const longitude = parseFloat(gpsData?.gpsValues?.longitude);
  const isValidCoordinate = !isNaN(latitude) && !isNaN(longitude);
  const currentCoordinate = isValidCoordinate
    ? { lat: latitude, lng: longitude }
    : defaultCoordinate;

  return (
    <div className="m-4">
      <div className="flex gap-2 mb-4 border-b">
        <button
          onClick={() => setActiveTab("live-data")}
          className={`px-4 py-2 font-medium ${
            activeTab === "live-data"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          Live Data
        </button>
        <button
          onClick={() => setActiveTab("gps")}
          className={`px-4 py-2 font-medium ${
            activeTab === "gps"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          GPS Data
        </button>
        <button
          onClick={() => setActiveTab("history")}
          className={`px-4 py-2 font-medium ${
            activeTab === "history"
              ? "border-b-2 border-primary text-primary"
              : "text-gray-500"
          }`}
        >
          History
        </button>
      </div>

      {activeTab === "live-data" && (
        <>
          <BatteryStatus
            socketData={lastUpdatedSocketData}
            lastUpdatedData={lastUpdatedData}
            lastUpdatedTime={lastUpdatedTime}
          />
          <DataMonitoring
            socketData={lastUpdatedSocketData}
            lastUpdatedData={lastUpdatedData}
          />
        </>
      )}

      {activeTab === "gps" && (
        <>
          <GPSInfo gpsData={gpsData} updatedTime={gpsUpdatedTime} />
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "500px" }}
              center={currentCoordinate}
              zoom={15}
            >
              {isValidCoordinate && (
                <>
                  <Marker
                    position={currentCoordinate}
                    icon={{
                      url: `${process.env.PUBLIC_URL}/bike-icon-bms.png`,
                      scaledSize: new window.google.maps.Size(50, 50),
                      anchor: new window.google.maps.Point(25, 48),
                    }}
                    onClick={() => setIsInfoWindowOpen(true)}
                    title="Current vehicle location"
                  />
                  {isInfoWindowOpen && (
                    <InfoWindow
                      position={currentCoordinate}
                      onCloseClick={() => setIsInfoWindowOpen(false)}
                    >
                      <div className="text-sm">
                        <p>
                          <span className="font-semibold">Time:</span>{" "}
                          {gpsUpdatedTime}
                        </p>
                        <p>
                          <span className="font-semibold">Satellites:</span>{" "}
                          {gpsData?.gpsValues?.numberOfGPSSattelite}
                        </p>
                        <p>
                          <span className="font-semibold">Latitude:</span>{" "}
                          {gpsData?.gpsValues?.latitute}
                        </p>
                        <p>
                          <span className="font-semibold">Longitude:</span>{" "}
                          {gpsData?.gpsValues?.longitude}
                        </p>
                        <p>
                          <span className="font-semibold">Altitude:</span>{" "}
                          {gpsData?.gpsValues?.altitude}
                        </p>
                        <p>
                          <span className="font-semibold">Course:</span>{" "}
                          {gpsData?.gpsValues?.course}
                        </p>
                        <p>
                          <span className="font-semibold">Speed:</span>{" "}
                          {gpsData?.gpsValues?.speed}
                        </p>
                      </div>
                    </InfoWindow>
                  )}
                </>
              )}
            </GoogleMap>
          ) : (
            <p>Loading map...</p>
          )}
        </>
      )}

      {activeTab === "history" && <DeviceHistory deviceId={deviceId} />}
    </div>
  );
};

export default DeviceInfo;
